import React, { useEffect, KeyboardEvent } from "react";
import { connect } from "react-redux";
import { Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { IApplicationState } from "src/~store/models/ApplicationState";
import { mailingItemInfoSelector, mailingRecipientsSelector } from "src/app/mailing/~store/selector";
import { IMailingRecipientsListProps } from "./~types/MailingRecipientsListProps";
import { getRecipients } from "src/app/mailing/~store/actions/actions";
import { Avatar } from '@mui/material';
import ContactIcon from '@mui/icons-material/AccountCircle';
import { GetContactAvatarLink } from "src/app/shared/helpers/avatar-helper/ContactAvatarLinkHelper";
import { GetChannelImage } from "src/app/shared/helpers/img-helpers/ImgHelper";
import { IMailingDeliveryStatus } from "./../../../~store/models/mailing-recipient";
import { IMessenger } from "src/app/contact/~store/models/Messenger";
import { onChatItemClick } from "src/app/chats/~store/actions/actions";
import './MailingRecipientsList.css';
import { setChatVisibilityToState } from "../../../../chats-panel/~store/actions/actions";
import { showErrorToastr } from "../../../../shared/helpers/toastr-helper/ToastrHelper";

const MailingRecipientsList = (props: ReturnType<typeof mapStateToProps> & IMailingRecipientsListProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        let mailingId = 0;
        let idFromUrl;
        if(window.location.pathname.includes('/mailing/mailing-recipients/')) {
            idFromUrl = window.location.pathname.split('/')[window.location.pathname.split('/').length - 1];
            mailingId = +idFromUrl;
        }
        if (isNaN(mailingId)) {
            showErrorToastr(t("mailing.mailingResultsReceivingByIdNotFoundMessage", { mailingId: idFromUrl }));
            navigate("/mailing");
            return;
        }
        props.getRecipients(mailingId, () => navigate("/mailing"));
    }, [location]);

    const _getRecipientMessengers = (messengers: IMessenger[]) => {
        return (
            messengers.map((messenger) => {
                return (
                    <div className="mailing-item-info__channel-row" key={messenger.messengerId}>
                        <img className="mailing-item-info__channel-image" src={GetChannelImage(messenger.channelModel.type)}/>
                        <span className="mailing-item-info__channel-externalid">{messenger.externalId}</span>
                    </div>
                );
            }));
    }

    const _getDeliveryStatses = (messengers: IMessenger[], deliveryStatuses: IMailingDeliveryStatus[]) => {
        return (
            messengers.map((messenger) => {
                const delivery = deliveryStatuses.find(d => d.messengerId === messenger.messengerId);
                const status = delivery?.chatId ? "Ok" : "Error";
                return (
                    <div className="mailing-item-info__channel-row">
                        {delivery && <div>
                            {status}
                            {status === "Error" && <span title={ t(delivery.errorMessage) } className="mailing-item-info__error-info">?</span>}
                        </div>}
                    </div>
                );
            })
        );
    }

    const _getChat = (messengers: IMessenger[], deliveryStatuses: IMailingDeliveryStatus[]) => {
        return (
            messengers.map((messenger) => {
                const delivery = deliveryStatuses.find(d => d.messengerId === messenger.messengerId);
                
                const openChat = () => {
                    if (delivery && props.selectedChatId !== delivery.chatId)
                    onChatItemClickHandler(delivery.chatId ?? 0);
                };

                const handleKeyUp = (e: KeyboardEvent<HTMLAnchorElement>) => {
                    if (e.key === "Enter") {
                        openChat();
                    }
                }

                return (
                    <div className="mailing-item-info__channel-row">
                        {delivery && <div>
                            {delivery.chatId ? <span>
                                <a 
                                    className="mailing-item-info__chat-link"
                                    onClick={openChat}
                                    tabIndex={0}
                                    onKeyUp={handleKeyUp}
                                >
                                    #{delivery.chatId}
                                </a>
                            </span> : <span></span>}
                        </div>}
                    </div>
                );
            })
        );
    }

    const onChatItemClickHandler = (chatId: number) => {
        props.onChatItemClick(chatId);

        setChatVisibilityToAll(true);
    }

    const setChatVisibilityToAll = (value: boolean) => {
        props.setChatVisibility(value);
        props.setChatVisibilityToState(value);
    };

    return (
        <>
            <div className="mailing-recipients-main__header">{t('mailing.mailingResult')}</div>
            <div 
                onScroll={(e) => {if(e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight) props.getRecipients(props.mailingItemInfo.mailingId);}} 
                className={"mailing-item-info__recipients-list-block " + (props.isChatVisible && "mailing-item-info__recipients-list-block-opened-chat-panel")}>
                <Table className="mailing-table card-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th className="mailing-item-header__contact">{t('common.contact')}</th>
                            <th>{t('common.messengers')}</th>
                            <th>{t('common.status')}</th>
                            <th>{t('common.chat')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.recipients.map((recipient, index) => {
                            return (
                                <tr key={recipient.mailingRecipientId}>
                                    <td className="mailing-item-info__avatar">
                                        <Avatar className="mailing-item-info__recipient-photo" src={GetContactAvatarLink(recipient.contact?.avatarId)}>
                                            <ContactIcon style={{ color: '#26a69a'}} />
                                        </Avatar>
                                    </td>
                                    <td className="mailing-item-info__recipient-name">
                                        <div>{recipient.contact?.firstName} {recipient.contact?.lastName} {recipient.contact?.patronymic}</div>
                                        <div>{recipient.contact?.chatName}</div>
                                        <div>{recipient.contact?.phone}</div>
                                    </td>
                                    <td className="mailing-item-info__messenger">
                                        {_getRecipientMessengers(recipient.contact?.messengers || [])}
                                    </td>
                                    <td className="mailing-item__mailing-status">
                                        {_getDeliveryStatses(recipient.contact?.messengers || [], recipient.deliveriesStatus) } 
                                    </td>
                                    <td className="mailing-item-info__link">
                                        {_getChat(recipient.contact?.messengers || [], recipient.deliveriesStatus)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </>
    );
}

const mapStateToProps = (state: IApplicationState, externalProps: any) => {
    return {
        mailingItemInfo: mailingItemInfoSelector(state),
        recipients: mailingRecipientsSelector(state),
        selectedChatId: state.chatItemsState.selectedChatId,
        isChatVisible: state.chatItemsState.isChatVisible,
        setChatVisibility: externalProps.setChatVisibility,
    };
};

const mapDispatchToProps = (dispatch: any): IMailingRecipientsListProps => {
    return {
        getRecipients: (mailingId: number, onGoBackToMailings?: () => void) => dispatch(getRecipients(mailingId, onGoBackToMailings)),
        onChatItemClick: (chatId: number) => dispatch(onChatItemClick(chatId)),
        setChatVisibilityToState: (value: boolean) => dispatch(setChatVisibilityToState(value))
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(MailingRecipientsList);